'use client'

import type { Header } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuth } from '@/providers/Auth'
import { MenuIcon } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { ACCOUNTS_ENABLED } from '@/lib/constants'

interface Props {
  menu: Header['clubNav'] | Header['shopNav']
  zone?: 'club' | 'shop'
}

export function MobileMenu({ menu, zone = 'club' }: Props) {
  const { user } = useAuth()

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  const closeMobileMenu = () => setIsOpen(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname, searchParams])

  return (
    <Sheet onOpenChange={setIsOpen} open={isOpen}>
      <SheetTrigger className="relative flex h-11 w-11 items-center justify-center rounded-md border border-neutral-200 text-black transition-colors dark:border-neutral-700 dark:bg-black dark:text-white">
        <MenuIcon className="h-4" />
      </SheetTrigger>

      <SheetContent side="left" className="px-4">
        <SheetHeader className="px-0 pt-4 pb-0">
          <SheetTitle>UtroClub</SheetTitle>

          <SheetDescription />
        </SheetHeader>

        <div className="py-4">
          {menu?.length ? (
            <ul className="flex w-full flex-col">
              {menu.map((item) => (
                <li className="py-2" key={item.id}>
                  <CMSLink {...item.link} appearance="link" />
                </li>
              ))}
            </ul>
          ) : null}

          {/* переход в другую зону сайта — всегда на виду */}
          <div className="mt-6 border-t border-ink/10 pt-6">
            {zone === 'club' ? (
              <Button asChild className="w-full">
                <Link href="/shop">Смотреть мерч</Link>
              </Button>
            ) : (
              <Button asChild variant="outline" className="w-full">
                <Link href="/">Вернуться в клуб</Link>
              </Button>
            )}
          </div>
        </div>

        {ACCOUNTS_ENABLED &&
          (user ? (
          <div className="mt-4">
            <h2 className="text-xl mb-4">Личный кабинет</h2>
            <hr className="my-2" />
            <ul className="flex flex-col gap-2">
              <li>
                <Link href="/orders">Заказы</Link>
              </li>
              <li>
                <Link href="/account/addresses">Адреса</Link>
              </li>
              <li>
                <Link href="/account">Настройки</Link>
              </li>
              <li className="mt-6">
                <Button asChild variant="outline">
                  <Link href="/logout">Выйти</Link>
                </Button>
              </li>
            </ul>
          </div>
        ) : (
          <div>
            <h2 className="text-xl mb-4">Личный кабинет</h2>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button asChild className="w-full sm:flex-1" variant="outline">
                <Link href="/login">Войти</Link>
              </Button>
              <span className="text-center text-sm text-muted-foreground sm:text-base">или</span>
              <Button asChild className="w-full sm:flex-1">
                <Link href="/create-account">Создать аккаунт</Link>
              </Button>
            </div>
          </div>
        ))}
      </SheetContent>
    </Sheet>
  )
}
